import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, useMediaQuery, useMultiStyleConfig, useStyleConfig} from '@chakra-ui/react'
import KeyFeaturesCardEditorial from './key-features-card-editorial'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Mousewheel} from 'swiper/modules'
import {breakpoints} from '../../../theme/_utils'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {useProducts} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useSlideUpAnimation from '../../../custom-hooks/use-slide-up-animation'
import SanitizedHtml from '../../html-sanitizer'

const KeyFeaturesEditorial = ({header_copy, body_copy, cards, isSlider = false, aspectRatio}) => {
    const [isDesktop] = useMediaQuery(`(min-width: ${breakpoints.lg})`)
    const {containerRef, getAnimProps} = useSlideUpAnimation()

    const categoriesData = useGetCategoriesData()
    const ids = cards
        ?.filter((c) => c.button?.productsfcc?.data?.length > 0)
        ?.map((c) => c.button.productsfcc.data[0].id)
        ?.filter(Boolean)

    const {data: productsData} = useProducts(
        {
            parameters: {
                ids: ids?.join(','),
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'key-features-index',
            },
        },
        {enabled: ids?.length > 0},
    )

    const slidesStyles = {
        base: {num: 1.5, gap: 6},
        md: {num: 2.3, gap: 8},
        lg: {num: 3.2, gap: 8},
    }

    const swiperStyles = useStyleConfig('Swiper')
    const styles = useMultiStyleConfig('KeyFeatures', {
        variant: isSlider ? 'slider' : 'default',
    })

    const {locale} = useMultiSite()

    return (
        <Box layerStyle="page" px="0" py={{base: 'spacing.lg', md: 'spacing.xlg'}}>
            <Flex {...styles.container} ref={containerRef} data-cs-label="editorial-key_features">
                <Flex {...styles.headerGrid} alignItems="center" gap="24px">
                    <SanitizedHtml
                        as={Text}
                        html={header_copy}
                        textAlign="center"
                        fontWeight="medium"
                        color="foreground.neutral.default"
                        textStyle="head-large"
                        fontFamily="heading"
                    />
                    {body_copy && (
                        <SanitizedHtml
                            as={Text}
                            html={body_copy}
                            textAlign="center"
                            fontWeight="normal"
                            color="foreground.neutral.default"
                            textStyle="body-large"
                        />
                    )}
                </Flex>
                {isSlider && !isDesktop ? (
                    <Box
                        __css={{
                            ...swiperStyles,
                            '.swiper-slide': {...swiperStyles['.swiper-slide'], height: 'auto'},
                        }}
                        justifyContent={'center'}
                    >
                        <Swiper
                            modules={[Mousewheel]}
                            slidesPerView={slidesStyles.base.num}
                            spaceBetween={slidesStyles.base.gap}
                            className="key-features-slider"
                            mousewheel={{forceToAxis: true, sensitivity: 1, releaseOnEdges: true}}
                        >
                            {cards &&
                                cards.map((card, index) => (
                                    <SwiperSlide key={card.uid}>
                                        <Box {...getAnimProps(index)} height="100%">
                                            <KeyFeaturesCardEditorial
                                                imageUrl={card?.image?.url}
                                                isSlider
                                                title={card?.header_copy}
                                                description={card?.body_copy}
                                                aspectRatio={aspectRatio}
                                            />
                                        </Box>
                                    </SwiperSlide>
                                ))}
                        </Swiper>
                    </Box>
                ) : (
                    <Flex
                        {...styles.cardsContainer}
                        justifyContent={{base: 'flex-start', lg: 'center'}}
                    >
                        {cards &&
                            cards.map((card, index) => {
                                const {url, label, isAbsolute} = getGlobalLink({
                                    link: card?.button,
                                    categoriesData,
                                    productsData,
                                    locale: locale?.alias || locale?.id?.toLowerCase(),
                                })

                                return (
                                    <Box key={card.uid} {...getAnimProps(index)}>
                                        <KeyFeaturesCardEditorial
                                            isLinkAbsolute={isAbsolute}
                                            imageUrl={card?.image?.url}
                                            images={card?.images}
                                            title={card?.header_copy}
                                            description={card?.body_copy}
                                            linkLabel={label}
                                            linkUrl={url}
                                            aspectRatio={aspectRatio}
                                        />
                                    </Box>
                                )
                            })}
                    </Flex>
                )}
            </Flex>
        </Box>
    )
}

KeyFeaturesEditorial.displayName = 'KeyFeaturesEditorial'

KeyFeaturesEditorial.propTypes = {
    header_copy: PropTypes.string,
    body_copy: PropTypes.string,
    cards: PropTypes.array,
    isSlider: PropTypes.bool,
    aspectRatio: PropTypes.string,
}

export default KeyFeaturesEditorial
